import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, ImagePlus, Loader2, Download, Trash2, X, Search, RefreshCw, Sparkles } from "lucide-react";
import { formatBRL, formatKM } from "../../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatBRL2 = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2 }).format(v);

const inputCls =
  "w-full bg-[#0d0f17] border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

export const AnunciosTab = ({ onUnauthorized, prefill, onPrefillConsumed }) => {
  const [anuncios, setAnuncios] = useState(null);
  const [busca, setBusca] = useState("");
  const [foto, setFoto] = useState(null);
  const [precoDigits, setPrecoDigits] = useState("");
  const [descricao, setDescricao] = useState("");
  const [formato, setFormato] = useState("quadrado");
  const [gerando, setGerando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/anuncios`, { headers });
      setAnuncios(res.data);
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else { setAnuncios([]); toast.error("Erro ao carregar anúncios."); }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!prefill) return;
    const fotoUrl = prefill.imagem || prefill.fotos?.[0] || "";
    if (fotoUrl) {
      setFoto({
        url: fotoUrl,
        preview: fotoUrl.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${fotoUrl}` : fotoUrl,
      });
    }
    if (prefill.preco) setPrecoDigits(String(Math.round(prefill.preco * 100)));
    setDescricao(`${prefill.nome} ${prefill.ano} • ${formatKM(prefill.km)} km • ${prefill.cambio} • ${prefill.combustivel}`);
    setResultado(null);
    onPrefillConsumed?.();
    toast.info("Anúncio pré-preenchido a partir do estoque — revise e gere.");
  }, [prefill]);

  const precoNum = precoDigits ? Number(precoDigits) / 100 : 0;

  const onPrecoChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
    setPrecoDigits(digits);
  };

  const enviarFoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Envie uma imagem (jpg, png, webp).");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, { headers });
      setFoto({ url: res.data.url, preview: URL.createObjectURL(file) });
      setResultado(null);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error("Erro no upload da foto.");
    } finally { setUploading(false); }
  };

  const gerar = async (e) => {
    e.preventDefault();
    if (!foto) return toast.error("Envie a foto do carro.");
    if (!precoNum) return toast.error("Informe o preço.");
    setGerando(true);
    try {
      const res = await axios.post(`${API}/anuncios`, {
        foto_url: foto.url,
        preco: precoNum,
        descricao,
        formato,
      }, { headers });
      setResultado(res.data);
      toast.success("Anúncio gerado!");
      load();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Erro ao gerar anúncio.");
    } finally { setGerando(false); }
  };

  const baixar = async (a) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${a.imagem}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "image/png" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `anuncio-seed-motors-${a.id.slice(0, 8)}.png`;
      link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao baixar a imagem."); }
  };

  const excluir = async (a) => {
    if (!window.confirm(`Excluir o anúncio de ${formatBRL(a.preco)}? A imagem será removida.`)) return;
    try {
      await axios.delete(`${API}/anuncios/${a.id}`, { headers });
      setAnuncios((as) => as.filter((x) => x.id !== a.id));
      if (resultado?.id === a.id) setResultado(null);
      toast.success("Anúncio excluído.");
    } catch { toast.error("Erro ao excluir anúncio."); }
  };

  const novoAnuncio = () => { setFoto(null); setPrecoDigits(""); setDescricao(""); setResultado(null); };

  const filtrados = (anuncios || []).filter((a) =>
    !busca.trim() || a.descricao.toLowerCase().includes(busca.trim().toLowerCase()) || formatBRL2(a.preco).includes(busca.trim())
  );

  return (
    <div data-testid="anuncios-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-white flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-emerald-400" /> Gerador de anúncios
          </h1>
          <p className="text-xs text-slate-400 mt-1">Foto do carro + preço + descrição = imagem pronta com a marca Seed Motors.</p>
        </div>
        <button onClick={load} data-testid="anuncios-refresh" className="inline-flex items-center gap-2 glass hover:border-emerald-500/50 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
          <RefreshCw className="w-4 h-4" /> Atualizar
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        <form onSubmit={gerar} className="glass rounded-3xl p-6 space-y-4" data-testid="anuncio-form">
          <input
            type="file" accept="image/*" className="hidden" ref={fileRef}
            onChange={(e) => enviarFoto(e.target.files?.[0])}
            data-testid="anuncio-input-foto"
          />
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); enviarFoto(e.dataTransfer.files?.[0]); }}
            data-testid="anuncio-dropzone"
            className={`border-2 border-dashed rounded-2xl min-h-44 flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors overflow-hidden ${dragOver ? "border-emerald-500 bg-emerald-500/5" : "border-slate-700 hover:border-emerald-500/50"}`}
          >
            {uploading ? (
              <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
            ) : foto ? (
              <img src={foto.preview} alt="Foto do carro" className="w-full h-52 object-cover" data-testid="anuncio-foto-preview" />
            ) : (
              <>
                <ImagePlus className="w-8 h-8 text-slate-500" />
                <p className="text-sm text-slate-400 font-medium">Arraste a foto do carro ou clique para escolher</p>
                <p className="text-[11px] text-slate-600">JPG, PNG ou WebP — quanto maior, melhor o resultado</p>
              </>
            )}
          </div>
          {foto && (
            <button type="button" onClick={() => { setFoto(null); setResultado(null); }} data-testid="anuncio-foto-remove" className="text-xs text-slate-500 hover:text-red-400 transition-colors">
              Trocar foto
            </button>
          )}

          <div>
            <label className="text-xs text-slate-400 block mb-1.5">Preço*</label>
            <input
              className={inputCls} inputMode="numeric" placeholder="R$ 0,00" required
              value={precoDigits ? formatBRL2(precoNum) : ""}
              onChange={onPrecoChange}
              data-testid="anuncio-input-preco"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1.5">Descrição (aparece no banner)</label>
            <textarea
              className={`${inputCls} resize-none`} rows={2} maxLength={120}
              placeholder="Ex: BMW X6 2024 • Único dono • Revisões em dia"
              value={descricao} onChange={(e) => setDescricao(e.target.value)}
              data-testid="anuncio-input-descricao"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1.5">Formato</label>
            <div className="grid grid-cols-2 gap-2" data-testid="anuncio-formato">
              {[
                { id: "quadrado", label: "Quadrado", detalhe: "1080×1080 · Instagram/WhatsApp" },
                { id: "paisagem", label: "Paisagem", detalhe: "1200×900 · sites e classificados" },
              ].map((f) => (
                <button
                  key={f.id} type="button"
                  onClick={() => setFormato(f.id)}
                  data-testid={`anuncio-formato-${f.id}`}
                  className={`rounded-xl border px-4 py-3 text-left transition-colors ${formato === f.id ? "border-emerald-500 bg-emerald-500/10" : "border-slate-800 hover:border-slate-600"}`}
                >
                  <p className={`text-sm font-semibold ${formato === f.id ? "text-emerald-400" : "text-white"}`}>{f.label}</p>
                  <p className="text-[11px] text-slate-500">{f.detalhe}</p>
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit" disabled={gerando || uploading}
            data-testid="anuncio-gerar-button"
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-6 py-4 rounded-full transition-colors"
          >
            {gerando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
            {gerando ? "Gerando anúncio..." : "Gerar Anúncio"}
          </button>
        </form>

        <div className="glass rounded-3xl p-6 flex flex-col" data-testid="anuncio-preview-card">
          <h2 className="font-display font-bold text-white mb-4">Pré-visualização</h2>
          {resultado ? (
            <div className="space-y-4 flex-1 flex flex-col">
              <img src={`${process.env.REACT_APP_BACKEND_URL}${resultado.imagem}`} alt="Anúncio gerado" className="w-full rounded-2xl" data-testid="anuncio-resultado-img" />
              <div className="flex gap-3 mt-auto">
                <button
                  onClick={() => baixar(resultado)} data-testid="anuncio-download-button"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold px-5 py-3.5 rounded-full transition-colors text-sm"
                >
                  <Download className="w-4 h-4" /> Baixar PNG
                </button>
                <button onClick={novoAnuncio} data-testid="anuncio-novo-button" className="inline-flex items-center gap-2 glass hover:border-emerald-500/50 text-white font-semibold px-5 py-3.5 rounded-full text-sm">
                  <X className="w-4 h-4" /> Novo
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-64 border border-dashed border-slate-800 rounded-2xl flex flex-col items-center justify-center gap-2 text-center p-6">
              <Megaphone className="w-8 h-8 text-slate-600" />
              <p className="text-sm text-slate-500" data-testid="anuncio-preview-vazio">Preencha o formulário e clique em Gerar Anúncio — a imagem final aparece aqui, com a logo no topo direito e o banner de preço embaixo.</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display text-lg font-bold text-white">Histórico de anúncios</h2>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            className="glass rounded-full pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-emerald-500 w-64"
            placeholder="Buscar por descrição ou preço..."
            value={busca} onChange={(e) => setBusca(e.target.value)}
            data-testid="anuncios-busca"
          />
        </div>
      </div>

      {anuncios === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <div key={i} className="h-64 rounded-2xl bg-[#131620] animate-pulse" />)}</div>
      ) : filtrados.length === 0 ? (
        <p className="text-slate-400 text-sm glass rounded-2xl p-8" data-testid="anuncios-empty">
          {busca ? "Nenhum anúncio encontrado para essa busca." : "Nenhum anúncio gerado ainda. Crie o primeiro acima!"}
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="anuncios-grid">
          <AnimatePresence>
            {filtrados.map((a) => (
              <motion.div
                key={a.id} layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                className="glass rounded-2xl overflow-hidden group" data-testid={`anuncio-card-${a.id}`}
              >
                <div className="relative">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${a.imagem}`} alt={a.descricao || "Anúncio"} className="w-full aspect-square object-cover" loading="lazy" />
                  <span className="absolute top-3 left-3 text-[10px] font-bold px-2 py-1 rounded-full bg-black/60 text-white backdrop-blur-sm">
                    {a.formato === "quadrado" ? "1080×1080" : "1200×900"}
                  </span>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-display font-bold text-emerald-400">{formatBRL(a.preco)}</p>
                    <p className="text-[11px] text-slate-500">{new Date(a.created_at).toLocaleDateString("pt-BR")}</p>
                  </div>
                  {a.descricao && <p className="text-xs text-slate-400 truncate">{a.descricao}</p>}
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => baixar(a)} data-testid={`anuncio-download-${a.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-400 text-xs font-semibold rounded-full py-2 transition-colors">
                      <Download className="w-3.5 h-3.5" /> Baixar
                    </button>
                    <button onClick={() => excluir(a)} data-testid={`anuncio-delete-${a.id}`} className="w-9 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/25 transition-colors" title="Excluir">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
